import { useState, useEffect } from "react";
import { Search, Loader2, Filter, Tag, Hash, Calendar, ChevronDown, ChevronUp, ShoppingCart, Download, Eye } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useEvents } from "@/events/hooks/useEvents";
import { useSearchPhotos } from "@/photos/hooks/usePhotos";
import { toastError, toastInfo, toastSuccess } from "@/lib/toast-utils";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useLocation } from "wouter";
import { useCartContext } from "@/contexts/CartContext";
import { FavoriteButton } from "@/components/FavoriteButton";
import { WatermarkedImage } from "@/components/ui/watermarked-image";

export default function AdvancedSearchPage() {
  const [, setLocation] = useLocation();
  
  // Inicializar estado com base na URL
  const getInitialState = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      bib: params.get('bib') || "",
      tags: params.get('tags') || "",
      eventId: params.get('eventId') || "",
      q: params.get('q') || ""
    };
  };

  const initialState = getInitialState();
  const [selectedEventId, setSelectedEventId] = useState<string>(initialState.eventId);
  const [bibNumber, setBibNumber] = useState<string>(initialState.bib);
  const [tags, setTags] = useState<string>(initialState.tags);
  const [isSearching, setIsSearching] = useState(false);
  const { addToCart, isLoading: cartLoading } = useCartContext();

  // Estado para busca com paginação agrupada
  interface SearchPage {
    id: number;
    photos: any[];
    isOpen: boolean;
  }

  const [searchPages, setSearchPages] = useState<SearchPage[]>([]);
  const [smartSearchQuery, setSmartSearchQuery] = useState(initialState.q || initialState.bib || "");
  const LIMIT = 20;

  // Buscar eventos disponíveis
  const { data: eventsResponse, isLoading: eventsLoading } = useEvents({ page: 1, limit: 50, status: 'Approved' });
  const events = eventsResponse?.events || [];

  // Hook para buscar fotos (busca real)
  const searchPhotosQuery = useSearchPhotos({ 
    eventId: selectedEventId && selectedEventId !== 'all' ? selectedEventId : undefined, 
    bib: (bibNumber || smartSearchQuery) ? (bibNumber || smartSearchQuery) : undefined,
    tags: tags ? tags.split(',').map(t => t.trim()) : undefined
  });

  const { data: searchResults, isLoading: searchLoading, error, refetch } = searchPhotosQuery;

  // Efeito para realizar busca se houver query 'q' ou 'bib' na URL
  useEffect(() => {
    if (initialState.q || initialState.bib || initialState.eventId) {
      handleSearch();
    }
  }, []);

  const handleSearch = () => {
    if (!bibNumber.trim() && !selectedEventId && !tags.trim() && !smartSearchQuery.trim()) {
      toastError({
        title: "Critérios insuficientes",
        description: "Por favor, informe um critério de busca.",
      });
      return;
    }

    setIsSearching(true);
    
    // Atualizar os filtros se houver smartSearchQuery
    if (smartSearchQuery && !bibNumber) {
      setBibNumber(smartSearchQuery);
    }
    
    refetch().then((result) => {
      setIsSearching(false);

      if (!result.data || result.data.length === 0) {
        toastInfo({
          title: "Nenhuma foto encontrada",
          description: "Tente ajustar seus critérios de busca.",
        });
        
        // Agrupar resultados em páginas
        setSearchPages([]);
      } else {
        toastSuccess({
          title: "Fotos encontradas!",
          description: `${result.data.length} foto(s) encontrada(s)!`,
        });
        
        // Agrupar resultados em páginas de 20 itens
        const photos = result.data;
        const pages: SearchPage[] = [];
        for (let i = 0; i < photos.length; i += LIMIT) {
          pages.push({
            id: Math.floor(i / LIMIT) + 1,
            photos: photos.slice(i, i + LIMIT),
            isOpen: i === 0 // Apenas a primeira página aberta
          });
        }
        setSearchPages(pages);
      }
    }).catch(() => {
      setIsSearching(false);
      toastError({
        title: "Erro na busca",
        description: "Erro ao buscar fotos. Tente novamente.",
      });
    });
  };

  const togglePage = (pageId: number) => {
    setSearchPages(prev => prev.map(p => 
      p.id === pageId ? { ...p, isOpen: !p.isOpen } : p
    ));
  };

  const groupPhotosByEvent = (photos: any[]) => {
    const grouped: { [key: string]: any[] } = {};
    photos.forEach(photo => {
      const eventTitle = photo.event?.title || "Outros";
      if (!grouped[eventTitle]) {
        grouped[eventTitle] = [];
      }
      grouped[eventTitle].push(photo);
    });
    return grouped;
  };

  const handleClearFilters = () => {
    setSelectedEventId("");
    setBibNumber("");
    setTags("");
    setSmartSearchQuery("");
  };

  const selectedEvent = events.find(event => event.id === selectedEventId);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h1 className="text-4xl font-bold mb-4 text-gray-900">Pesquisa Avançada</h1>
            <p className="text-lg text-gray-600">
              Encontre suas fotos usando múltiplos critérios de busca
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar de Filtros */}
            <div className="lg:col-span-1">
              <Card className="sticky top-24">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Filter className="w-5 h-5" />
                    Filtros
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Evento */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      Evento
                    </label>
                    <Select value={selectedEventId} onValueChange={setSelectedEventId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Todos os eventos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos os eventos</SelectItem>
                        {eventsLoading ? (
                          <SelectItem value="loading" disabled>Carregando...</SelectItem>
                        ) : (
                          events.map((event) => (
                            <SelectItem key={event.id} value={event.id}>
                              {event.title}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <Separator />

                  {/* Número do Peito */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <Hash className="w-4 h-4 text-gray-500" />
                      Número do Peito
                    </label>
                    <Input
                      type="text"
                      placeholder="Ex: 1234"
                      value={bibNumber}
                      onChange={(e) => setBibNumber(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                  </div>

                  <Separator />

                  {/* Tags */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <Tag className="w-4 h-4 text-gray-500" />
                      Tags
                    </label>
                    <Input
                      type="text"
                      placeholder="Ex: largada, chegada, pódio"
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <p className="text-xs text-muted-foreground">Separe por vírgulas</p>
                  </div>

                  <div className="pt-4 space-y-3">
                    <Button 
                      onClick={handleSearch} 
                      className="w-full bg-primary hover:bg-teal-600"
                      disabled={isSearching || searchLoading}
                    >
                      {isSearching || searchLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Search className="w-4 h-4 mr-2" />
                      )}
                      Buscar Fotos
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={handleClearFilters}
                      className="w-full"
                    >
                      Limpar Filtros
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Resultados */}
            <div className="lg:col-span-3">
              {/* Barra de busca inteligente */}
              <div className="mb-6">
                 <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <Input 
                      placeholder="Busca inteligente (Evento, Tag ou Número)" 
                      className="pl-10"
                      value={smartSearchQuery}
                      onChange={(e) => setSmartSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                 </div>
              </div>

              {/* Loader inicial ou erro */}
              {(searchLoading || isSearching) && searchPages.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                  <p className="text-lg text-gray-600">Buscando suas fotos...</p>
                </div>
              ) : error ? (
                <div className="text-center py-20 bg-white rounded-lg shadow-sm border">
                  <p className="text-red-600 text-lg">Erro ao buscar fotos. Tente novamente.</p>
                </div>
              ) : searchPages.length > 0 ? (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-semibold">
                      Resultados ({searchPages.reduce((acc, p) => acc + p.photos.length, 0)})
                    </h2>
                    {selectedEvent && (
                      <Badge variant="secondary" className="text-sm px-3 py-1">
                        {selectedEvent.title}
                      </Badge>
                    )}
                  </div>
                  
                  {/* Renderização de Busca com Páginas Colapsáveis */}
                  <div className="space-y-4">
                    {searchPages.map((pageItem) => (
                      <Collapsible
                        key={pageItem.id}
                        open={pageItem.isOpen}
                        onOpenChange={() => togglePage(pageItem.id)}
                        className="border rounded-lg bg-white shadow-sm"
                      >
                        <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-gray-50 transition-colors">
                          <h3 className="text-lg font-medium text-gray-900">
                            Página {pageItem.id} ({pageItem.photos.length} fotos)
                          </h3>
                          {pageItem.isOpen ? (
                            <ChevronUp className="h-5 w-5 text-gray-500" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-500" />
                          )}
                        </CollapsibleTrigger>
                        <CollapsibleContent className="p-4 pt-0">
                          <div className="space-y-8">
                            {Object.entries(groupPhotosByEvent(pageItem.photos)).map(([eventTitle, photos]) => (
                              <div key={eventTitle}>
                                <div className="flex items-center gap-3 mb-4 mt-4">
                                  <div className="w-10 h-10 bg-gray-200 rounded-md flex items-center justify-center">
                                    <Calendar className="w-5 h-5 text-gray-500" />
                                  </div>
                                  <h4 className="text-md font-semibold text-gray-800">{eventTitle}</h4>
                                  <Badge variant="outline" className="ml-2">{photos.length}</Badge>
                                </div>
                                
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                  {photos.map((photo: any) => {
                                    const photoEvent = selectedEvent || events.find(e => e.id === photo.eventId) || photo.event;
                                    const isFreeEvent = photoEvent?.monetizationType === 'free';
                                    const photoPrice = Number(photoEvent?.pricePerPhoto || photoEvent?.price || photo.price || 0);

                                    return (
                                      <Card key={photo.id} className="overflow-hidden hover:shadow-lg transition-all duration-300 group">
                                        <div className="relative aspect-[3/2]">
                                          <WatermarkedImage
                                            src={photo.watermarkedUrl || photo.storageUrl}
                                            alt={`Foto ${photo.filename}`}
                                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                            watermark={true}
                                            variant="thumbnail"
                                          />
                                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <FavoriteButton
                                              itemId={photo.id}
                                              itemType="photo"
                                              size="sm"
                                              className="bg-white/90 hover:bg-white backdrop-blur-sm shadow-sm"
                                            />
                                          </div>
                                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center p-4">
                                            <Button
                                              size="sm"
                                              variant="secondary"
                                              className="w-full bg-white/90 hover:bg-white text-black"
                                              onClick={() => setLocation(`/photos/${photo.id}`)}
                                            >
                                              <Eye className="w-4 h-4 mr-2" />
                                              Ver Detalhes
                                            </Button>
                                          </div>
                                        </div>
                                        <CardContent className="p-4">
                                          <div className="flex justify-between items-start mb-2">
                                            <div>
                                              <p className="text-xs text-muted-foreground font-mono">
                                                #{photo.filename.replace('.jpg', '').replace('photo_', '').substring(0, 8)}...
                                              </p>
                                            </div>
                                            {isFreeEvent ? (
                                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                                Grátis
                                              </Badge>
                                            ) : (
                                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                                R$ {photoPrice.toFixed(2)}
                                              </Badge>
                                            )}
                                          </div>
                                          
                                          <Button
                                            className={`w-full mt-2 ${
                                              isFreeEvent 
                                                ? 'bg-green-600 hover:bg-green-700' 
                                                : 'bg-primary hover:bg-teal-700'
                                            }`}
                                            disabled={cartLoading}
                                            onClick={async (e) => {
                                              e.preventDefault();
                                              if (isFreeEvent) {
                                                setLocation(`/photos/${photo.id}`);
                                              } else {
                                                try {
                                                  await addToCart(photo.id, 1);
                                                } catch (error) {
                                                  // Erro tratado no context
                                                }
                                              }
                                            }}
                                          >
                                            {isFreeEvent ? (
                                              <><Download className="w-4 h-4 mr-2" /> Download</>
                                            ) : (
                                              <><ShoppingCart className="w-4 h-4 mr-2" /> Comprar</>
                                            )}
                                          </Button>
                                        </CardContent>
                                      </Card>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-lg border border-dashed">
                  <Search className="h-16 w-16 text-gray-200 mb-4" />
                  <h3 className="text-xl font-medium text-gray-900 mb-2">Comece sua busca</h3>
                  <p className="text-gray-500 max-w-md text-center">
                    Use os filtros à esquerda ou a busca inteligente acima para encontrar suas fotos.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
